/**
 * DxfLeftRail — compact navigation rail for the Tecnico hub.
 *
 * Combines categories (compact pill strip + "Tutti" reset) with the
 * drawings list in a single 280px column. Previous layout split
 * categories (200px) + list (flex) across two columns, which left the
 * mechanical drawing preview squeezed into 320-420px. Merging them
 * frees the entire right canvas for the actual drawing — the page hero.
 *
 * The rail is internally scrollable so the user can navigate a large
 * library (~150 SMB-typical max) without the page itself scrolling.
 * Categories sit at the top (sticky), drawings fill the rest.
 *
 * Visual rhythm matches ``finance.tsx`` panels (paper texture,
 * border-hairline, rounded-lg) so the Tecnico hub feels like a sibling.
 */

import { useMemo } from "react";
import { plural } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { FolderOpen } from "lucide-react";
import { cn } from "@ando/ui";
import type { DxfCategory, DxfFile } from "@ando/core";
import { DxfFileRow } from "./DxfFileRow";
import { PANEL_SURFACE_CLASS } from "./DxfPreviewStage";

export interface DxfLeftRailProps {
  readonly files: readonly DxfFile[];
  readonly categories: readonly DxfCategory[];
  readonly selectedFileId: string | null;
  readonly selectedCategory: string | null;
  readonly totalCount: number;
  /** Current query — used for filter logic + empty-state copy. The
   *  input itself lives in the SurfaceLayout toolbar (June 2026
   *  uniformity pass). */
  readonly searchValue: string;
  readonly isLoading: boolean;
  readonly onSelectFile: (file: DxfFile) => void;
  readonly onSelectCategory: (name: string | null) => void;
  readonly className?: string;
}

/**
 * Top-N categories shown inline; rest collapses under "+N altre" expansion.
 * 6 fits comfortably in a 280px column even with longer labels (e.g.
 * "Bulloneria M6-M12").
 */
const TOP_CATEGORIES_VISIBLE: number = 6;

export function DxfLeftRail({
  files,
  categories,
  selectedFileId,
  selectedCategory,
  totalCount,
  searchValue,
  isLoading,
  onSelectFile,
  onSelectCategory,
  className,
}: DxfLeftRailProps) {
  const { t, i18n } = useLingui();
  // Top-N by file_count so the most-used buckets are always one tap away.
  // Sort descending; ties broken alphabetically for stability across runs.
  const topCategories = useMemo<readonly DxfCategory[]>(
    () =>
      [...categories]
        .sort((a, b) => {
          if (b.file_count !== a.file_count) {
            return b.file_count - a.file_count;
          }
          return a.name.localeCompare(b.name);
        })
        .slice(0, TOP_CATEGORIES_VISIBLE),
    [categories],
  );

  return (
    <aside
      aria-label={t`Disegni e categorie`}
      className={cn(
        "flex h-full min-h-0 w-full flex-col gap-3 p-3",
        PANEL_SURFACE_CLASS,
        className,
      )}
    >
      {/* Search lives in the SurfaceLayout toolbar now (June 2026
          uniformity pass — single ``WorkspaceSearchBar`` per tab).
          The rail still receives ``searchValue`` so it can keep
          filtering and rendering the empty-state when the query is
          live. */}

      {/* Categories pill row */}
      <nav
        aria-label={t`Categorie`}
        className="flex flex-wrap items-center gap-1.5"
      >
        <CategoryPill
          label={t`Tutti`}
          count={totalCount}
          isSelected={selectedCategory === null}
          onClick={() => onSelectCategory(null)}
          accented
        />
        {topCategories.map((c) => (
          <CategoryPill
            key={c.name}
            label={c.name}
            count={c.file_count}
            isSelected={selectedCategory === c.name}
            onClick={() => onSelectCategory(c.name)}
          />
        ))}
        {categories.length > TOP_CATEGORIES_VISIBLE ? (
          <details className="group">
            <summary
              aria-label={i18n._(
                plural(categories.length - TOP_CATEGORIES_VISIBLE, {
                  one: "Mostra l'altra # categoria",
                  other: "Mostra le altre # categorie",
                }),
              )}
              className="inline-flex cursor-pointer items-center gap-1.5 rounded-full border border-hairline bg-card/30 px-2.5 py-0.5 font-medium text-[11px] text-ink-3 transition-colors hover:bg-card/55 hover:text-ink-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/40 focus-visible:ring-offset-2 focus-visible:ring-offset-cream motion-reduce:transition-none"
            >
              <span>
                <Trans>+{categories.length - TOP_CATEGORIES_VISIBLE} altre</Trans>
              </span>
              <span className="rounded-full px-1.5 py-0.5 text-[10px] text-ink-3 tabular-nums">
                {categories
                  .slice(TOP_CATEGORIES_VISIBLE)
                  .reduce((s, c) => s + c.file_count, 0)}
              </span>
            </summary>
            <ul className="mt-1.5 flex flex-wrap gap-1.5">
              {categories.slice(TOP_CATEGORIES_VISIBLE).map((c) => (
                <li key={c.name}>
                  <CategoryPill
                    label={c.name}
                    count={c.file_count}
                    isSelected={selectedCategory === c.name}
                    onClick={() => onSelectCategory(c.name)}
                  />
                </li>
              ))}
            </ul>
          </details>
        ) : null}
      </nav>

      {/* Drawing list — flexible, scrollable. ``min-h-0`` is critical
          so flex children can shrink below their content height and let
          ``overflow-y-auto`` actually engage. */}
      <div className="flex min-h-0 flex-1 flex-col">
        <div className="mb-1.5 flex items-center justify-between px-1 font-medium text-[10px] text-ink-3 uppercase tracking-[0.05em]">
          <span className="flex items-center gap-1">
            <FolderOpen size={11} strokeWidth={1.75} aria-hidden />
            {selectedCategory ?? t`Tutti i disegni`}
          </span>
          <span className="tabular-nums">{files.length}</span>
        </div>

        {isLoading ? (
          <ul
            className="flex flex-col gap-1"
            aria-busy
            aria-label={t`Caricamento disegni`}
            role="status"
          >
            {[0, 1, 2, 3].map((i) => (
              <li
                key={i}
                aria-hidden
                className="h-12 animate-pulse rounded-lg bg-card/40 motion-reduce:animate-none"
              />
            ))}
          </ul>
        ) : files.length === 0 ? (
          <div className="flex flex-1 items-center justify-center rounded-lg border border-hairline border-dashed bg-card/30 p-4 text-center text-[11px] text-ink-3">
            {searchValue.trim() || selectedCategory ? (
              <Trans>
                Nessun disegno corrisponde
                <br />
                ai filtri attivi
              </Trans>
            ) : (
              <Trans>Carica un DXF dalla chat per iniziare</Trans>
            )}
          </div>
        ) : (
          <ol
            aria-label={t`Disegni CAD`}
            className="flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto pr-1"
          >
            {files.map((f) => (
              <li key={f.content_item_id}>
                <DxfFileRow
                  file={f}
                  isSelected={selectedFileId === f.content_item_id}
                  onSelect={onSelectFile}
                />
              </li>
            ))}
          </ol>
        )}
      </div>
    </aside>
  );
}
DxfLeftRail.displayName = "DxfLeftRail";


// ─────────────────────────────────────────────────────────────────────
// CategoryPill — compact selectable chip
// ─────────────────────────────────────────────────────────────────────


function CategoryPill({
  label,
  count,
  isSelected,
  onClick,
  accented = false,
  muted = false,
}: {
  label: string;
  count: number;
  isSelected: boolean;
  onClick: () => void;
  accented?: boolean;
  muted?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={isSelected}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 font-medium text-[11px] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/40 focus-visible:ring-offset-2 focus-visible:ring-offset-cream motion-reduce:transition-none",
        isSelected
          ? accented
            ? "border-ando-orange/40 bg-ando-orange-soft text-ando-orange"
            : "border-ink-2/30 bg-ink-2 text-white"
          : muted
            ? "border-hairline bg-card/30 text-ink-3 hover:bg-card/55 hover:text-ink-2"
            : "border-hairline bg-card/60 text-ink-2 hover:bg-card/85 hover:text-ink-1",
      )}
    >
      <span className="max-w-[120px] truncate">{label}</span>
      <span
        className={cn(
          "rounded-full px-1.5 py-0.5 text-[10px] tabular-nums",
          isSelected
            ? accented
              ? "bg-ando-orange/20"
              : "bg-white/15"
            : "text-ink-3",
        )}
      >
        {count}
      </span>
    </button>
  );
}
